/*
 * Invoices, payments, quotes, leads and the public pay and quote pages (draft 3). Money fields end in Cents and are whole
 * cents. Public answers carry only what a client may see.
 */
import { z } from "zod";
import { clientLook } from "./client-look.js";
import { listQuery } from "./api.zod.js";
import { dateString } from "./clients.zod.js";
import { INVOICE_STATES, PAID_EDIT_CHOICES, PAYMENT_METHODS, PAYMENT_STATUSES, QUOTE_STATUSES } from "./billing.js";
import { publicProposal } from "./proposals.zod.js";
import { publicAutoQuote, publicBooking, quoteBookInput } from "./quote-booking.js";
import { lineLink } from "./prices.js";
import { quoteSection, sectionFile } from "./quote-sections.js";

const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((s) => (s === "" ? null : s))
    .nullable()
    .optional();

const money = z.number().int().min(-10_000_000).max(10_000_000);

// Invoices -------------------------------------------------------------------------------------------------------

export const invoiceLineInput = z.object({
  description: z.string().trim().min(1, "Name the line.").max(200),
  note: optionalText(500),
  link: lineLink,
  quantity: z.number().positive("Use a number above 0.").max(100_000).default(1),
  unitPriceCents: money,
  serviceDate: dateString.nullable().optional(),
});
export type InvoiceLineInput = z.infer<typeof invoiceLineInput>;

export const invoiceLineRow = z.object({
  id: z.uuid(),
  visitId: z.uuid().nullable(),
  description: z.string(),
  note: z.string().nullable(),
  link: z.string().nullable().optional(),
  quantity: z.number(),
  unitPriceCents: z.number(),
  amountCents: z.number(),
  serviceDate: z.string().nullable(),
  propertyLabel: z.string().nullable(),
});
export type InvoiceLineRow = z.infer<typeof invoiceLineRow>;

export const invoiceRow = z.object({
  id: z.uuid(),
  /** Given on the first send; null while in To check. */
  number: z.number().nullable(),
  clientId: z.uuid(),
  clientName: z.string(),
  status: z.string(),
  state: z.enum(INVOICE_STATES),
  issuedOn: z.string().nullable(),
  dueAt: z.string().nullable(),
  sentAt: z.string().nullable(),
  subtotalCents: z.number(),
  tipCents: z.number(),
  totalCents: z.number(),
  paidCents: z.number(),
  balanceCents: z.number(),
  visitCount: z.number(),
  firstServiceDate: z.string().nullable(),
  jobName: z.string().nullable(),
  /** What it is for: the job, else its line items, as the old Steward's Subject column. */
  subject: z.string(),
  /** A bank or Klarna payment is on its way (a pending payment). */
  onTheWay: z.boolean(),
  /** The client opened the pay page. */
  viewed: z.boolean(),
  /** Paid or closed without ever being sent to the client. */
  notSent: z.boolean(),
});
export type InvoiceRow = z.infer<typeof invoiceRow>;

export const paymentRow = z.object({
  id: z.uuid(),
  invoiceId: z.uuid().nullable(),
  invoiceNumber: z.number().nullable(),
  clientId: z.uuid(),
  clientName: z.string(),
  amountCents: z.number(),
  tipCents: z.number(),
  method: z.enum(PAYMENT_METHODS),
  checkNumber: z.string().nullable(),
  status: z.string(),
  receivedAt: z.string(),
  recordedBy: z.string().nullable(),
  note: z.string().nullable(),
});
export type PaymentRow = z.infer<typeof paymentRow>;

export const invoiceEditRow = z.object({
  kind: z.string(),
  fromTotalCents: z.number(),
  toTotalCents: z.number(),
  what: z.string().nullable(),
  by: z.string().nullable(),
  at: z.string(),
});

/**
 * One automatic follow-up (invoice reminder, debt reminder, quote follow-up): one that went out (channel and to empty when
 * the old Steward sent it), failed, or the next one due.
 */
export const followUpRow = z.object({
  kind: z.string(),
  channel: z.string(),
  to: z.string(),
  status: z.enum(["sent", "failed", "next"]),
  at: z.string(),
});
export type FollowUpRow = z.infer<typeof followUpRow>;

export const cardOnFile = z.object({ brand: z.string(), last4: z.string(), expMonth: z.number(), expYear: z.number() });
export type CardOnFile = z.infer<typeof cardOnFile>;

export const invoiceDetail = invoiceRow.extend({
  poNumber: z.string().nullable(),
  /** A hand-typed invoice nobody worked (question 54). */
  nobodyWorked: z.boolean(),
  isBusiness: z.boolean(),
  noteToClient: z.string().nullable(),
  payNote: z.string().nullable(),
  debt: z.boolean(),
  remindersOff: z.boolean(),
  /** The client said a check is in the mail (reminders wait 10 days from then). */
  checkMailedAt: z.string().nullable(),
  firstSentAt: z.string().nullable(),
  sentVia: z.string().nullable(),
  sentTo: z.string().nullable(),
  payLink: z.string(),
  lines: z.array(invoiceLineRow),
  visits: z.array(z.object({ ref: z.string(), date: z.string(), jobName: z.string() })),
  /** Who worked an invoice typed by hand (no visits), so payroll pays them. Empty on invoices with visits. */
  crew: z.array(z.object({ id: z.uuid(), name: z.string() })),
  payments: z.array(paymentRow),
  edits: z.array(invoiceEditRow),
  followUps: z.array(followUpRow).default([]),
  client: z.object({
    id: z.uuid(),
    name: z.string(),
    firstName: z.string(),
    phone: z.string().nullable(),
    email: z.string().nullable(),
    address: z.string().nullable(),
    creditCents: z.number(),
    card: cardOnFile.nullable(),
    autoCharge: z.boolean(),
  }),
  crewCollected: z.string().nullable(),
  /** The ways the pay page offers: this invoice's own, else Settings (own tells which). */
  payWays: z.object({ card: z.boolean(), bank: z.boolean(), klarna: z.boolean(), own: z.boolean() }),
  /** Klarna can show: one-time work, not charged by itself, and at least KLARNA_MIN_CENTS. */
  klarnaFits: z.boolean(),
  createdAt: z.string(),
  viewedAt: z.string().nullable(),
  paidAt: z.string().nullable(),
  /**
   * Other invoices of this client this one can be put together with (merge): ones in To check, plus a sent one not paid
   * yet when one of the two is still in To check (the old Steward's Add to invoice).
   */
  mergeWith: z.array(
    z.object({
      id: z.uuid(),
      number: z.number().nullable(),
      status: z.string(),
      totalCents: z.number(),
      firstServiceDate: z.string().nullable(),
    }),
  ),
  /** Waiting to charge the saved card by itself: when, or held by the office (the old autobill Hold). */
  autoChargeAt: z.string().nullable(),
  autoChargeHeld: z.boolean(),
});
export type InvoiceDetail = z.infer<typeof invoiceDetail>;

/** Hold keeps an invoice from charging the saved card by itself; taking it off charges it at the next minute. */
export const autoChargeHoldBody = z.object({ hold: z.boolean() }).strict();

/** The list's status filter: a tab, or the old Steward's To collect and Payment on the way. */
export const INVOICE_FILTERS = [...INVOICE_STATES, "to_collect", "on_the_way"] as const;
export type InvoiceFilter = (typeof INVOICE_FILTERS)[number];

export const invoiceListQuery = listQuery.extend({
  state: z.enum([...INVOICE_FILTERS, "all"]).default("all"),
  /** Issued (sent, else the service date) on or after and on or before these days. */
  from: dateString.optional(),
  to: dateString.optional(),
  clientId: z.uuid().optional(),
  /** One job's invoices (its phone page, like Jobber's), including ones that also hold another job's visits. */
  jobId: z.uuid().optional(),
  q: z.string().trim().max(100).optional(),
});
export type InvoiceListQuery = z.infer<typeof invoiceListQuery>;

/** The Invoices page's top cards, like the old Steward's: Overview, Waiting on payment, Issued and Average invoice. */
export const invoiceSummary = z.object({
  toCheck: z.number(),
  /** Done visits on month-end jobs, waiting for the month's invoice. */
  monthEnd: z.number(),
  toCollect: z.number(),
  awaiting: z.number(),
  onTheWay: z.number(),
  pastDue: z.number(),
  /** What each Overview line comes to (Jobber shows the money next to each count). */
  toCheckCents: z.number(),
  toCollectCents: z.number(),
  awaitingCents: z.number(),
  onTheWayCents: z.number(),
  pastDueCents: z.number(),
  /** Sent and not paid (debts left out). */
  owedCount: z.number(),
  owedCents: z.number(),
  /** Sent in the past 30 days, and their totals without tips. */
  issuedCount: z.number(),
  issuedCents: z.number(),
  /** The same for the 30 days before that, so the cards can say up or down. */
  issuedBeforeCount: z.number(),
  issuedBeforeCents: z.number(),
  /** Average days from first sent to paid, for invoices paid in the past 30 days (null when none), and the 30 before. */
  payDays: z.number().nullable(),
  payDaysBefore: z.number().nullable(),
});
export type InvoiceSummary = z.infer<typeof invoiceSummary>;

export const invoiceList = z.object({
  invoices: z.array(invoiceRow),
  counts: z.record(z.enum(INVOICE_STATES), z.number()),
  summary: invoiceSummary,
  nextCursor: z.string().nullable(),
});

/** Person ids of who worked an invoice typed by hand. */
const invoiceCrew = z.array(z.uuid()).max(20);

/** Changing an unpaid invoice. Lines replace all lines. */
export const invoicePatch = z
  .object({
    lines: z.array(invoiceLineInput).min(1, "An invoice needs a line.").max(100),
    poNumber: optionalText(60),
    noteToClient: optionalText(2000),
    remindersOff: z.boolean(),
    /** A check is in the mail (true) or the mark comes off (false). */
    checkMailed: z.boolean(),
    debt: z.boolean(),
    /** Who worked it, for an invoice typed by hand only (question 43). */
    crew: invoiceCrew,
    /** A hand-typed invoice nobody worked: pays nobody, not on No crew yet. */
    nobodyWorked: z.boolean().optional(),
    /** How this invoice can be paid, this invoice only (card is always on). Null goes back to Settings. */
    payWays: z
      .object({ card: z.literal(true), bank: z.boolean(), klarna: z.boolean() })
      .strict()
      .nullable(),
  })
  .partial();
export type InvoicePatch = z.infer<typeof invoicePatch>;

/** Changing a paid invoice (owner and manager). A lower total needs choice. */
export const invoiceEditPaidBody = z.object({
  lines: z.array(invoiceLineInput).min(1).max(100),
  noteToClient: optionalText(2000),
  choice: z.enum(PAID_EDIT_CHOICES).optional(),
});
export type InvoiceEditPaidBody = z.infer<typeof invoiceEditPaidBody>;

export const invoiceNewBody = z.object({
  clientId: z.uuid(),
  lines: z.array(invoiceLineInput).min(1).max(100),
  noteToClient: optionalText(2000),
  crew: invoiceCrew.optional(),
});

export const mergeInvoicesBody = z.object({ ids: z.array(z.uuid()).min(2).max(20) });
/** Takes one visit off an invoice that has more than one: its lines go on a new invoice in To check. */
export const takeVisitOffBody = z.object({ visitId: z.uuid() });

export const SEND_VIA = ["text", "email"] as const;
export const sendPreviewQuery = z.object({ via: z.enum(SEND_VIA).optional() });
export const sendPreview = z.object({
  via: z.enum(SEND_VIA),
  to: z.string().nullable(),
  /** Every number or email the client has for this way of sending. */
  choices: z.array(z.string()),
  subject: z.string().nullable(),
  body: z.string(),
  /** Plain warnings, like a number that belongs to another client too. */
  warnings: z.array(z.string()),
  /** Email: whether a PDF copy goes attached unless changed here (the client's own setting). */
  pdf: z.boolean(),
});
export type SendPreview = z.infer<typeof sendPreview>;

export const sendBody = z.object({
  via: z.enum(SEND_VIA),
  to: z.string().trim().min(3).max(200),
  /** Send later, at this moment (owner, question 36: for odd cases). The invoice waits as Scheduled. */
  sendAt: z.iso.datetime().optional(),
  /** Email: copies to the client's other addresses (only ones on file, up to 5). */
  cc: z.array(z.email().max(200)).max(5).optional(),
  /** Email: a copy to the person sending it. */
  copyMe: z.boolean().optional(),
  /** Email: a PDF copy attached. Left out: the client's own setting. */
  pdf: z.boolean().optional(),
});
export type SendBody = z.infer<typeof sendBody>;

export const recordPaymentBody = z.object({
  method: z.enum(["cash", "check"]),
  amountCents: z.number().int().min(1).max(10_000_000),
  checkNumber: optionalText(40),
  receivedOn: dateString.optional(),
  note: optionalText(500),
});
export type RecordPaymentBody = z.infer<typeof recordPaymentBody>;

/** Charging a saved card by hand: the owner repeats the amount and card they were shown, and says yes. */
export const chargeCardBody = z.object({
  amountCents: z.number().int().min(50).max(10_000_000),
  last4: z.string().regex(/^\d{4}$/),
  confirm: z.literal(true),
});
export type ChargeCardBody = z.infer<typeof chargeCardBody>;

export const paymentListQuery = listQuery.extend({
  from: dateString.optional(),
  to: dateString.optional(),
  method: z.enum(PAYMENT_METHODS).optional(),
  status: z.enum(PAYMENT_STATUSES).optional(),
  /** One client's payments (their page's Billing card). */
  clientId: z.uuid().optional(),
  /** Payments on one job's invoices. */
  jobId: z.uuid().optional(),
  /** Client name, invoice number or check number. */
  q: z.string().trim().max(100).optional(),
  limit: z.coerce.number().int().min(1).max(1000).default(500),
});
/** A row on the Payments list: the payment plus how it came in. */
export const paymentListRow = paymentRow.extend({ paidThrough: z.string(), refund: z.boolean(), online: z.boolean() });
export type PaymentListRow = z.infer<typeof paymentListRow>;

/** The Payments page's top cards (old Steward plus Jobber), for the whole company whatever the filters. */
export const paymentSummary = z.object({
  monthCents: z.number(),
  monthCount: z.number(),
  monthTipCents: z.number(),
  /** Average days from sent to paid, invoices paid in the past 30 days; null when none. */
  payDays: z.number().nullable(),
  payDaysBefore: z.number().nullable(),
  /** Share of invoices paid in the past 60 days that were paid before they were past due (0 to 100). */
  onTimePct: z.number().nullable(),
  onTimePctBefore: z.number().nullable(),
  owedCents: z.number(),
  owedCount: z.number(),
});
export type PaymentSummary = z.infer<typeof paymentSummary>;

export const paymentList = z.object({
  payments: z.array(paymentListRow),
  /** All matching payments, when more than shown. */
  count: z.number(),
  totalCents: z.number(),
  tipCents: z.number(),
  summary: paymentSummary,
});
export type PaymentList = z.infer<typeof paymentList>;

export const paymentDetail = paymentListRow.extend({
  client: z.object({
    id: z.uuid(),
    name: z.string(),
    phone: z.string().nullable(),
    email: z.string().nullable(),
    address: z.string().nullable(),
  }),
  visitDate: z.string().nullable(),
  /** Stripe's number for it, to find it in Stripe. */
  transaction: z.string().nullable(),
  refundOf: z.uuid().nullable(),
  timeline: z.array(z.object({ at: z.string(), what: z.string(), amountCents: z.number().nullable() })),
});
export type PaymentDetail = z.infer<typeof paymentDetail>;

export const clientBillingPatch = z
  .object({
    autoCharge: z.boolean(),
    autoChargeAgreedOn: dateString.nullable(),
    paysBy: z.enum(["card", "cash", "check", "bank"]).nullable(),
    /** Email invoices with a PDF copy attached. */
    pdfInvoices: z.boolean(),
  })
  .partial();

/** Bill part of a one-time job early (a progress payment), on an invoice of its own. */
export const progressBody = z.object({ amountCents: z.number().int().min(100).max(10_000_000) }).strict();

/** Send a receipt for a paid invoice by hand: a text, or an email with the receipt PDF. */
export const receiptBody = z.object({ via: z.enum(SEND_VIA), to: z.string().trim().min(3).max(200) }).strict();

/** A link for the client to save a card (Stripe's own page), texted to them when send is on. */
export const cardLinkBody = z.object({ send: z.boolean().default(false) }).strict();
export const cardLinkAnswer = z.object({ url: z.string(), sent: z.boolean() });

// Public pay page ------------------------------------------------------------------------------------------------

export const publicBrand = z.object({
  /** The brand's name, never the location's. */
  name: z.string(),
  color: z.string().nullable(),
  phone: z.string().nullable(),
  email: z.string().nullable(),
  /** The brand's footer links from Settings (Facebook, Google, Yelp and the rest). */
  links: z.array(z.object({ label: z.string(), url: z.string() })).default([]),
  /** The old Steward's colors and white logo (client-look.ts). */
  look: clientLook.optional(),
  address: z.string().nullable().default(null),
  /** The brand's website, for the Privacy Policy link. */
  website: z.string().nullable().default(null),
});

/** Our own save a card page (api billing/card-page.ts): the brand, who it is for and the card on file now. */
export const publicCardPage = z.object({
  brand: publicBrand,
  firstName: z.string(),
  card: z.object({ brand: z.string(), last4: z.string() }).nullable(),
});
export type PublicCardPage = z.infer<typeof publicCardPage>;

export const publicInvoice = z.object({
  brand: publicBrand,
  number: z.number().nullable(),
  firstName: z.string(),
  /** Who it is for, as the old pay page's invoice card shows them. */
  client: z.object({ name: z.string(), address: z.string().nullable(), phone: z.string().nullable() }).optional(),
  /** What it is for (the job's name). */
  jobName: z.string().nullable().default(null),
  /** The home it is for ("2 bed, 2 bath, 5 other rooms, 2,200 sq ft"), home cleaning only. */
  home: z.string().nullable().default(null),
  issuedOn: z.string().nullable(),
  dueAt: z.string().nullable().default(null),
  /** Stripe's key for the card fields, so they show from the start. Null when cards go through the test card. */
  publishableKey: z.string().nullable().default(null),
  status: z.enum(["open", "paid", "void"]),
  lines: z.array(
    z.object({
      description: z.string(),
      note: z.string().nullable(),
      link: z.string().nullable().default(null),
      quantity: z.number(),
      amountCents: z.number(),
      serviceDate: z.string().nullable(),
    }),
  ),
  poNumber: z.string().nullable(),
  noteToClient: z.string().nullable(),
  subtotalCents: z.number(),
  tipCents: z.number(),
  totalCents: z.number(),
  paidCents: z.number(),
  balanceCents: z.number(),
  /** Empty when the company has tips off (and always for the agency). */
  tipChoices: z.array(z.object({ percent: z.number(), cents: z.number() })),
  /** Paid with no tip yet: a tip can still be added until this day (TIP_AFTER_DAYS after paying), else null. */
  tipUntil: z.string().nullable(),
  maxTipCents: z.number(),
  /** Card pay works only when the company has its Stripe key saved. */
  canPayCard: z.boolean(),
  /** The ways offered on the pay page (card, bank, Klarna), from Settings and this invoice. */
  payWays: z.array(z.enum(["card", "bank", "klarna"])),
  /** A bank payment on its way (3 to 5 business days): the page says so and takes no other payment meanwhile. */
  bankOnTheWayCents: z.number(),
  saveCardSentence: z.string(),
  cardOnFile: z.object({ brand: z.string(), last4: z.string() }).nullable(),
  /** Charges after each visit for this client (autoChargeOffer): the pay page says so (offer) or asks with a box (ask). */
  autoCharge: z.enum(["on", "offer", "ask", "none"]),
  /** The visit's photos, only where the brand shows them on the pay page (mowing proof, question 37). room is the yard
   * it shows (yard-spots.ts) or a cleaning room; the page shows only a yard. */
  photos: z.array(z.object({ id: z.uuid(), name: z.string(), room: z.string().nullable() })),
  /** How to pay by cash or check, from Settings. Empty: nothing shows. */
  payNote: z.string().default(""),
  /** The company's small print at the bottom of the invoice. */
  footer: z.string().default(""),
});
export type PublicInvoice = z.infer<typeof publicInvoice>;

/** A tip after paying: its own payment on the pay page. */
export const tipStartBody = z.object({ tipCents: z.number().int().min(1).max(10_000_000) }).strict();

export const payStartBody = z.object({
  tipCents: z.number().int().min(0).max(10_000_000).default(0),
  saveCard: z.boolean().default(false),
  /** The client ticked Charge my card after each visit (the ask box). */
  autoCharge: z.boolean().default(false),
  /** The way picked with the page's buttons (Credit / Debit, Bank payment, Klarna): the payment takes only that way. */
  way: z.enum(["card", "bank", "klarna"]).optional(),
});
/**
 * The card is taken on our own pay page (owner, question 32): Stripe's card fields with the company's publishable key and
 * this payment's client secret. fake: no Stripe here (a PC, the preview), the page shows a test Pay button instead.
 */
export const payStartAnswer = z.object({
  intentId: z.string(),
  clientSecret: z.string(),
  publishableKey: z.string().nullable(),
  fake: z.boolean(),
});
export type PayStart = z.infer<typeof payStartAnswer>;
export const payDoneBody = z.object({ paymentIntent: z.string().min(5).max(200) });
/** Take card by phone: the office saved the card in Stripe's card box; the server checks it with Stripe. */
export const phoneCardDoneBody = z.object({ intentId: z.string().min(5).max(200) }).strict();
export const phoneCardDoneAnswer = z.object({ saved: z.boolean() });
export const payDoneAnswer = z.object({ paid: z.boolean() });
/**
 * Pay with the card saved before (owner, 2026-11-01): one press on the pay page. The page sends back the balance and the
 * card's last 4 it showed, so a changed card or balance is refused and the page looks again.
 */
export const paySavedBody = z
  .object({
    tipCents: z.number().int().min(0).max(10_000_000).default(0),
    amountCents: z.number().int().min(50).max(10_000_000),
    last4: z.string().regex(/^\d{4}$/),
    /** The client ticked Charge my card after each visit (the ask box). */
    autoCharge: z.boolean().default(false),
  })
  .strict();
export const paySavedAnswer = z.object({ paid: z.boolean(), failure: z.string().nullable() });

/** Money back to a client (owner, question 33): part or all of one payment. Card payments go back on the card. */
export const refundBody = z
  .object({
    paymentId: z.uuid(),
    amountCents: z.number().int().positive().max(100_000_000),
    reason: z.string().trim().min(1, "Say why.").max(300),
    /** Off (the default): the invoice total comes down too, so the client does not owe it again. */
    keepOwed: z.boolean().default(false),
  })
  .strict();

// Quotes ---------------------------------------------------------------------------------------------------------

export const quoteLineInput = z.object({
  description: z.string().trim().min(1, "Name the line.").max(200),
  note: optionalText(500),
  link: lineLink,
  quantity: z.number().positive().max(100_000).default(1),
  unitPriceCents: money,
  optional: z.boolean().default(false),
  /** For optional lines, picked to start with. */
  picked: z.boolean().default(false),
  recurring: z.boolean().default(false),
  /** Good, Better or Best: the option this line belongs to (it is optional too). The client picks one option. */
  optionGroup: optionalText(40),
  /** once, or monthly for an agency plan's retainer line (agency proposals, draft 13). */
  bill: z.enum(["once", "monthly"]).default("once"),
});
export type QuoteLineInput = z.infer<typeof quoteLineInput>;

export const quoteLineRow = z.object({
  id: z.uuid(),
  description: z.string(),
  note: z.string().nullable(),
  link: z.string().nullable().optional(),
  quantity: z.number(),
  unitPriceCents: z.number(),
  amountCents: z.number(),
  optional: z.boolean(),
  picked: z.boolean(),
  recurring: z.boolean(),
  optionGroup: z.string().nullable(),
  bill: z.enum(["once", "monthly"]),
});
export type QuoteLineRow = z.infer<typeof quoteLineRow>;

const depositInput = z.object({ kind: z.enum(["percent", "amount"]), value: z.number().min(0).max(10_000_000) }).nullable();

const quoteFields = {
  clientId: z.uuid().nullable().optional(),
  leadId: z.uuid().nullable().optional(),
  contactName: optionalText(120),
  contactPhone: optionalText(30),
  contactEmail: z.email().nullable().optional(),
  address: optionalText(300),
  title: optionalText(120),
  message: optionalText(2000),
  /** A service agreement the client signs with the quote (commercial bids). */
  terms: optionalText(8000),
  /** A card must be saved on the quote page before the client can approve. */
  cardRequired: z.boolean().optional(),
  /** The price list answers the quote was priced from, so the client can change rooms, how often and add-ons. */
  answers: z.record(z.string(), z.unknown()).nullable().optional(),
  expiresOn: dateString.nullable().optional(),
  deposit: depositInput.optional(),
  frequency: z.string().max(20).default("one-time"),
};

export const quoteInput = z
  .object({ ...quoteFields, lines: z.array(quoteLineInput).min(1, "A quote needs a line.").max(50) })
  .refine((q) => Boolean(q.clientId) || Boolean(q.contactName), {
    message: "Pick a client or type a name.",
    path: ["contactName"],
  });
export type QuoteInput = z.infer<typeof quoteInput>;

export const quotePatch = z.object({ ...quoteFields, lines: z.array(quoteLineInput).min(1).max(50) }).partial();
export type QuotePatch = z.infer<typeof quotePatch>;

export const quoteRow = z.object({
  id: z.uuid(),
  number: z.number(),
  name: z.string(),
  clientId: z.uuid().nullable(),
  title: z.string().nullable(),
  status: z.enum(QUOTE_STATUSES),
  totalCents: z.number(),
  sentAt: z.string().nullable(),
  approvedAt: z.string().nullable(),
  expiresOn: z.string().nullable(),
  createdAt: z.string(),
  /** Where the work is: the quote's address, else the client's. */
  address: z.string().nullable().optional(),
  /** The client opened the quote page at least once. */
  viewed: z.boolean().optional(),
  /** A plan offer, not a quote the office priced. */
  offer: z.boolean().optional(),
});
export type QuoteRow = z.infer<typeof quoteRow>;

export const quoteDetail = quoteRow.extend({
  leadId: z.uuid().nullable(),
  contactName: z.string().nullable(),
  contactPhone: z.string().nullable(),
  contactEmail: z.string().nullable(),
  address: z.string().nullable(),
  message: z.string().nullable(),
  deposit: z.object({ kind: z.enum(["percent", "amount"]), value: z.number() }).nullable(),
  depositCents: z.number(),
  frequency: z.string(),
  /** What it was priced from (the website's or Price it fast's answers), so Price it fast opens with them on Edit. */
  answers: z.record(z.string(), z.unknown()).nullable().default(null),
  lines: z.array(quoteLineRow),
  /** Drawing is the hand signature as a PNG data link, or null for a typed name only (older quotes, or signing turned off). */
  signature: z.object({ name: z.string(), at: z.string(), drawing: z.string().nullable() }).nullable(),
  jobId: z.uuid().nullable(),
  depositInvoiceId: z.uuid().nullable(),
  link: z.string(),
  /** The brand's name the client sees on the quote text, never the location's. */
  brand: z.string().default(""),
  terms: z.string().nullable(),
  cardRequired: z.boolean(),
  /** How many times the client opened the quote, and when last. */
  views: z.number(),
  lastViewedAt: z.string().nullable(),
  followUps: z.array(followUpRow).default([]),
  /** Made on WORK > Proposals: Edit opens the proposal builder. */
  proposal: z.boolean(),
  /** Added parts (introduction, images, attachments, reviews) and the files they use (quote-sections.ts). */
  sections: z.array(quoteSection).default([]),
  sectionFiles: z.array(sectionFile).default([]),
});
export type QuoteDetail = z.infer<typeof quoteDetail>;

export const quoteListQuery = listQuery.extend({
  status: z.enum([...QUOTE_STATUSES, "all"]).default("all"),
  /** One client's quotes (the client page's Work overview). */
  clientId: z.uuid().optional(),
  /**
   * Plan offers (owner, 2026-11-04): left out of the quote list unless asked, "only" for Quotes, Plan offers. A client's
   * own list shows both.
   */
  offers: z.enum(["hide", "only", "all"]).optional(),
});

const statWindow = z.object({ count: z.number(), cents: z.number() });
/** The Quotes page's top cards (shared/pipeline.ts quoteStats). */
export const quoteStatsShape = z.object({
  counts: z.record(z.enum(QUOTE_STATUSES), z.number()),
  sent: statWindow,
  sentBefore: statWindow,
  converted: statWindow,
  convertedBefore: statWindow,
  rate: z.number().nullable(),
  rateBefore: z.number().nullable(),
});

export const quoteSendBody = z.object({
  via: z.enum(SEND_VIA),
  to: z.string().trim().min(3).max(200),
  /** The words as the office changed them in Send quote; the quote link is added when they took it out. */
  message: z.string().trim().min(1).max(1500).optional(),
});

export const quoteConvertBody = z.object({
  startDate: dateString,
  startTime: z
    .string()
    .regex(/^\d{2}:\d{2}$/)
    .nullable()
    .optional(),
});
export const quoteConvertAnswer = z.object({ jobId: z.uuid(), clientId: z.uuid() });

export const publicQuote = z.object({
  brand: publicBrand,
  /** Added parts with something in them, and their files (opened at public/quote/:token/files/:id). */
  sections: z.array(quoteSection).default([]),
  sectionFiles: z.array(sectionFile).default([]),
  number: z.number(),
  firstName: z.string(),
  /** A client's quote (not a lead's): the page shows My account, which opens their hub from this link. */
  account: z.boolean().default(false),
  title: z.string().nullable(),
  /** The home it is for ("2 bed, 2 bath, 5 other rooms, 2,200 sq ft"), home cleaning only. */
  home: z.string().nullable().default(null),
  message: z.string().nullable(),
  status: z.enum(QUOTE_STATUSES),
  expiresOn: z.string().nullable(),
  lines: z.array(
    z.object({
      id: z.uuid(),
      description: z.string(),
      note: z.string().nullable(),
      link: z.string().nullable().default(null),
      quantity: z.number(),
      amountCents: z.number(),
      optional: z.boolean(),
      picked: z.boolean(),
      recurring: z.boolean(),
      optionGroup: z.string().nullable(),
      bill: z.enum(["once", "monthly"]),
    }),
  ),
  /** A commercial proposal: the cover, letter, scope and terms above the price. Null for every other quote. */
  proposal: publicProposal.nullable(),
  /** An agency plan: the agreement's words and the auto pay box (draft 13). Null for every other quote. */
  agreement: z
    .object({
      words: z.array(z.string()),
      autoPayWords: z.string(),
      monthlyCents: z.number(),
      setupCents: z.number(),
      /** Once signed: whether they ticked auto pay. */
      autoPay: z.boolean().nullable(),
    })
    .nullable(),
  deposit: z.object({ kind: z.enum(["percent", "amount"]), value: z.number() }).nullable(),
  /** Drawing is the hand signature as a PNG data link, or null for a typed name only (older quotes, or signing turned off). */
  signature: z.object({ name: z.string(), at: z.string(), drawing: z.string().nullable() }).nullable(),
  /** After approval with a deposit, the deposit's pay page. */
  payLink: z.string().nullable(),
  terms: z.string().nullable(),
  /** A card must be saved before approving (signed card terms; Stripe checks the card first). */
  cardRequired: z.boolean(),
  /** The client signs with a finger or mouse to approve (Settings, Quotes). */
  signRequired: z.boolean(),
  hasCard: z.boolean(),
  saveCardSentence: z.string(),
  /** The policies the client agrees to by signing (Settings, Quotes), or the ones they signed once it is approved. */
  policies: z.array(z.object({ key: z.string(), title: z.string(), text: z.string() })),
  /** Rooms, how often and add-ons the client can change (quotes priced from the price list only). */
  answers: z.record(z.string(), z.unknown()).nullable(),
  services: z.array(z.object({ id: z.string(), name: z.string() })),
  extras: z.array(z.object({ id: z.string(), name: z.string(), each: z.boolean() })),
  /** An auto quote the client can change on the page (a home from the price list, or a lawn). */
  auto: publicAutoQuote.nullable(),
  /** Booking a day on the page, when the quote is open and the company books from quotes. */
  booking: publicBooking.nullable(),
  /** Booked from the page: the first visit's day and arrival window words. */
  bookedFor: z.object({ date: z.string(), window: z.string().nullable() }).nullable(),
  /** A job the office booked: the day and place it is held for, waiting on the client's signature (and card). */
  confirmFor: z
    .object({ date: z.string(), time: z.string().nullable(), often: z.string(), address: z.string().nullable().optional() })
    .nullable()
    .optional(),
});
export type PublicQuote = z.infer<typeof publicQuote>;

/** Change the home on the quote page: the price list prices it again. */
export const quoteChangeBody = z.object({ answers: z.record(z.string(), z.unknown()) }).strict();

/** A commercial bid priced by hours (FEATURE-MAP 9, the OctoClean method). */
export const commercialBidBody = z
  .object({
    clientId: z.uuid().nullable().optional(),
    contactName: optionalText(120),
    contactPhone: optionalText(30),
    contactEmail: z.email().nullable().optional(),
    address: optionalText(300),
    title: z.string().trim().min(1).max(120),
    /** The cleaner's wage an hour, in cents. */
    wageCents: z.number().int().min(100).max(20_000),
    /** Labor times this covers taxes, supplies and profit: 1.3 to 1.5. */
    factor: z.number().min(1.3).max(1.5),
    hoursPerVisit: z.number().min(0.25).max(24),
    visitsPerWeek: z.number().min(0.25).max(14),
    suppliesCents: z.number().int().min(0).max(1_000_000).default(0),
    /** What is cleaned and how often, in plain words. */
    scope: z.string().trim().max(4000),
    terms: optionalText(8000),
    cardRequired: z.boolean().default(false),
  })
  .strict();
export type CommercialBid = z.infer<typeof commercialBidBody>;

/** A hand signature from the quote page's pad: a small PNG as a data link (a few KB, capped well above that). */
export const signatureDrawing = z
  .string()
  .max(300_000, "That signature is too big. Clear it and sign again.")
  .regex(/^data:image\/png;base64,[A-Za-z0-9+/]+=*$/, "Sign in the box.");

export const quoteApproveBody = z.object({
  picked: z.array(z.uuid()).max(50).default([]),
  signature: z.string().trim().min(2, "Type your full name to sign.").max(120),
  /** The hand signature drawn on the page, as a PNG data link. Needed when the company asks for a signature. */
  drawing: signatureDrawing.nullable().default(null),
  /** Agency plans: the auto pay box was ticked (the written OK to charge the saved card each month). */
  autoPay: z.boolean().default(false),
  /** An auto quote booked on the page: the day picked goes on the schedule. */
  book: quoteBookInput.optional(),
});

// Leads and the pipeline -----------------------------------------------------------------------------------------

export const leadInput = z.object({
  name: z.string().trim().min(1, "Type a name.").max(120),
  phone: optionalText(30),
  email: z.email().nullable().optional(),
  address: optionalText(300),
  service: optionalText(120),
  valueCents: z.number().int().min(0).max(10_000_000).nullable().optional(),
  source: optionalText(40),
  stageId: z.uuid().optional(),
  note: optionalText(2000),
  /** Agency deals: the business, its website and who on the team owns the deal (draft 13). */
  businessName: optionalText(160),
  website: optionalText(300),
  assignedTo: z.uuid().nullable().optional(),
});
export type LeadInput = z.infer<typeof leadInput>;

export const leadPatch = leadInput.partial().extend({ stageId: z.uuid().optional() });

export const leadRow = z.object({
  id: z.uuid(),
  name: z.string(),
  phone: z.string().nullable(),
  email: z.string().nullable(),
  /** On one line, for reading. */
  address: z.string().nullable(),
  /** The same address in parts, to fill a client's address box. */
  place: z.object({
    street1: z.string().nullable(),
    street2: z.string().nullable(),
    city: z.string().nullable(),
    state: z.string().nullable(),
    zip: z.string().nullable(),
  }),
  service: z.string().nullable(),
  valueCents: z.number().nullable(),
  source: z.string().nullable(),
  stageId: z.uuid(),
  clientId: z.uuid().nullable(),
  quoteId: z.uuid().nullable(),
  note: z.string().nullable(),
  businessName: z.string().nullable(),
  website: z.string().nullable(),
  assignedTo: z.uuid().nullable(),
  stageChangedAt: z.string(),
  createdAt: z.string(),
  /** On the pipeline only: who it is assigned to, and the quote made for it (its own, else its newest). */
  assignedName: z.string().nullable().optional(),
  quote: z
    .object({ id: z.uuid(), number: z.number(), status: z.enum(QUOTE_STATUSES), totalCents: z.number() })
    .nullable()
    .optional(),
});
export type LeadRow = z.infer<typeof leadRow>;

const ruleDays = z.number().int().min(1, "Use 1 day or more.").max(3650).nullable();
/** A column's own settings (shared/pipeline.ts). */
export const stageRulesShape = z.object({
  hideAfterDays: ruleDays,
  moveAfterDays: ruleDays,
  moveToStageId: z.uuid().nullable(),
  followUpDays: ruleDays,
});
export const pipelineStage = z.object({
  id: z.uuid(),
  name: z.string(),
  position: z.number(),
  fixed: z.string().nullable(),
  rules: stageRulesShape.optional(),
});
export const pipeline = z.object({
  stages: z.array(pipelineStage),
  leads: z.array(leadRow),
  /** Leads kept off the board by each column's "hide after" rule, by stage id. Empty when older ones were asked for. */
  hidden: z.record(z.string(), z.number()).optional(),
});
export type Pipeline = z.infer<typeof pipeline>;
export const pipelineQuery = z.object({ older: z.enum(["0", "1"]).default("0") });

// Home ----------------------------------------------------------------------------------------------------------

export const moneyToDo = z.object({
  toCheck: z.number(),
  toCheckCents: z.number(),
  /** Everything still owed, past due included. */
  awaitingCents: z.number(),
  awaiting: z.number(),
  pastDue: z.number(),
  pastDueCents: z.number(),
  /** Cash or check the crew should pick up. */
  toCollect: z.number(),
  quotesDraft: z.number(),
  quotesWaiting: z.number(),
  quotesWaitingCents: z.number(),
  quotesApproved: z.number(),
  quotesApprovedCents: z.number(),
});
export type MoneyToDo = z.infer<typeof moneyToDo>;

// Walkthroughs (draft 9): notes and photos taken at the place before a bid. ------------------------------------------

export const walkthroughInput = z
  .object({
    clientId: z.uuid().nullable().optional(),
    leadId: z.uuid().nullable().optional(),
    quoteId: z.uuid().nullable().optional(),
    day: dateString,
    /** When to be there (14:30). With a time it is planned ahead and shows on the Schedule. */
    time: z
      .string()
      .regex(/^\d{2}:\d{2}$/, "Use a time like 14:30.")
      .nullable()
      .optional(),
    /** Who goes. */
    assignedTo: z.uuid().nullable().optional(),
    address: optionalText(300),
    notes: z.string().trim().max(8000).default(""),
  })
  .strict();
/** Change a walkthrough: another day, time or person, or the notes after it. */
export const walkthroughPatch = z
  .object({
    day: dateString.optional(),
    time: z
      .string()
      .regex(/^\d{2}:\d{2}$/, "Use a time like 14:30.")
      .nullable()
      .optional(),
    assignedTo: z.uuid().nullable().optional(),
    address: optionalText(300),
    notes: z.string().trim().max(8000).optional(),
  })
  .strict();
export const walkthroughRow = z.object({
  id: z.uuid(),
  clientId: z.uuid().nullable(),
  leadId: z.uuid().nullable(),
  quoteId: z.uuid().nullable(),
  day: z.string(),
  /** 14:30, or null when no time was set. */
  time: z.string().nullable(),
  assignedTo: z.uuid().nullable(),
  /** The person going, by name. */
  who: z.string().nullable(),
  /** The lead or client it is for, by name (the Schedule's line). */
  forName: z.string().nullable(),
  address: z.string().nullable(),
  notes: z.string(),
  photos: z.array(z.object({ id: z.uuid(), name: z.string() })),
  createdAt: z.string(),
});
export type WalkthroughRow = z.infer<typeof walkthroughRow>;
